import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import './TestSuiteDetail.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function TestSuiteDetail() {
  const { projectKey, suiteId } = useParams();
  const navigate = useNavigate();
  const [suite, setSuite] = useState(null);
  const [linkedTestCases, setLinkedTestCases] = useState([]);
  const [availableTestCases, setAvailableTestCases] = useState([]);
  const [folders, setFolders] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedTestCases, setSelectedTestCases] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    loadSuiteData();
  }, [suiteId]);

  // Auto-dismiss toast after 5 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const loadSuiteData = async () => {
    try {
      setLoading(true);
      // Get project first
      const token = localStorage.getItem('token');
      const projectRes = await fetch(`${API_URL}/projects/${projectKey}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!projectRes.ok) {
        setError('Failed to load project');
        setLoading(false);
        return;
      }
      
      const projectData = await projectRes.json();
      const project = projectData.data || projectData;
      
      if (!project || !project._id) {
        setError('Project not found');
        setLoading(false);
        return;
      }

      // Get suite details
      const suiteRes = await api.get(`/suites/${suiteId}`);
      setSuite(suiteRes.data);

      // Get all test cases for this project
      const allTestCasesRes = await api.get(`/testcases?projectId=${project._id || project.id}`);
      const allTestCases = allTestCasesRes.data.data || allTestCasesRes.data;

      // Get folders
      const foldersRes = await api.get(`/folders?projectId=${project._id || project.id}&type=testcase`);
      setFolders(foldersRes.data.data || foldersRes.data || []);

      // Filter test cases by suite - convert IDs to strings for comparison
      const linked = allTestCases.filter(tc => {
        const tcSuiteId = tc.suiteId?._id || tc.suiteId || tc.suite?._id || tc.suite;
        return tcSuiteId && tcSuiteId.toString() === suiteId;
      });
      const available = allTestCases.filter(tc => {
        const tcSuiteId = tc.suiteId?._id || tc.suiteId || tc.suite?._id || tc.suite;
        return !tcSuiteId || tcSuiteId.toString() !== suiteId;
      });

      setLinkedTestCases(linked);
      setAvailableTestCases(available);
      setLoading(false);
    } catch (err) {
      console.error('Error loading suite data:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load suite details');
      setLoading(false);
    }
  };

  const handleUnlinkTestCase = async (testCaseId) => {
    if (!window.confirm('Are you sure you want to unlink this test case from the suite?')) {
      return;
    }

    try {
      await api.put(`/testcases/${testCaseId}`, {
        suiteId: null
      });
      setToast({ type: 'success', message: 'Test case unlinked successfully' });
      loadSuiteData();
    } catch (err) {
      console.error('Error unlinking test case:', err);
      setToast({ type: 'error', message: 'Failed to unlink test case' });
    }
  };

  const handleLinkTestCases = async () => {
    if (selectedTestCases.length === 0) {
      setToast({ type: 'error', message: 'Please select at least one test case' });
      return;
    }

    setIsLinking(true);
    try {
      // Link selected test cases to this suite - convert IDs to strings
      await Promise.all(
        selectedTestCases.map(tcId => {
          const testCaseId = typeof tcId === 'object' ? (tcId._id || tcId.id) : tcId;
          return api.put(`/testcases/${testCaseId.toString()}`, {
            suiteId: suiteId
          });
        })
      );
      setToast({ type: 'success', message: `Successfully linked ${selectedTestCases.length} test case(s) to this suite` });
      setShowLinkModal(false);
      setSelectedTestCases([]);
      setSearchTerm('');
      loadSuiteData();
    } catch (err) {
      console.error('Error linking test cases:', err);
      setToast({ type: 'error', message: `Failed to link test cases: ${err.response?.data?.message || err.message || 'Unknown error'}` });
    } finally {
      setIsLinking(false);
    }
  };

  const handleUnlinkAll = async () => {
    if (!linkedTestCases || linkedTestCases.length === 0) {
      setToast({ type: 'error', message: 'No linked test cases to unlink' });
      return;
    }

    if (!window.confirm(`Are you sure you want to unlink all ${linkedTestCases.length} test case(s) from this suite?`)) {
      return;
    }

    setIsUnlinking(true);
    try {
      await Promise.all(
        linkedTestCases.map(tc => {
          const id = tc._id || tc.id;
          return api.put(`/testcases/${id.toString()}`, { suiteId: null });
        })
      );
      setToast({ type: 'success', message: `Unlinked ${linkedTestCases.length} test case(s)` });
      loadSuiteData();
    } catch (err) {
      console.error('Error unlinking all test cases:', err);
      setToast({ type: 'error', message: `Failed to unlink test cases: ${err.response?.data?.message || err.message || 'Unknown error'}` });
    } finally {
      setIsUnlinking(false);
    }
  };

  const toggleTestCaseSelection = (testCaseId) => {
    setSelectedTestCases(prev =>
      prev.includes(testCaseId)
        ? prev.filter(id => id !== testCaseId)
        : [...prev, testCaseId]
    );
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const buildFolderTree = (folders) => {
    const folderMap = {};
    const rootFolders = [];
    
    // Create a map of all folders
    folders.forEach(folder => {
      folderMap[folder._id || folder.id] = { ...folder, children: [] };
    });
    
    // Build the tree structure
    folders.forEach(folder => {
      const folderId = folder._id || folder.id;
      if (folder.parentId) {
        const parent = folderMap[folder.parentId];
        if (parent) {
          parent.children.push(folderMap[folderId]);
        } else {
          rootFolders.push(folderMap[folderId]);
        }
      } else {
        rootFolders.push(folderMap[folderId]);
      }
    });
    
    return rootFolders;
  };

  const getTestCasesForFolder = (folderId) => {
    return availableTestCases.filter(tc => {
      const tcFolderId = tc.folderId?._id || tc.folderId || tc.folder?._id || tc.folder;
      return tcFolderId && tcFolderId.toString() === folderId.toString();
    });
  };

  const getTestCasesWithoutFolder = () => {
    return availableTestCases.filter(tc => !tc.folderId && !tc.folder);
  };

  const renderFolderTree = (folderTree) => {
    return folderTree.map(folder => {
      const folderId = folder._id || folder.id;
      const isExpanded = expandedFolders.has(folderId);
      const testCasesInFolder = getTestCasesForFolder(folderId);
      
      // Filter test cases by search term
      const filteredTestCases = searchTerm
        ? testCasesInFolder.filter(tc =>
            tc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            tc.testCaseId.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : testCasesInFolder;
      
      // When searching, hide folders with no matching test cases and no children
      const hasMatchingContent = filteredTestCases.length > 0 || folder.children.length > 0;
      if (searchTerm && !hasMatchingContent) return null;
      
      return (
        <div key={folderId} className="folder-item">
          <div className="folder-header" onClick={() => toggleFolder(folderId)}>
            <span className="folder-icon">{isExpanded ? '📂' : '📁'}</span>
            <span className="folder-name">{folder.name}</span>
            <span className="folder-count">({filteredTestCases.length})</span>
          </div>
          {isExpanded && (
            <div className="folder-content">
              {filteredTestCases.map(tc => (
                <div key={tc._id || tc.id} className="test-case-item">
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedTestCases.includes(tc._id || tc.id)}
                      onChange={() => toggleTestCaseSelection(tc._id || tc.id)}
                    />
                    <span className="test-case-id">{tc.testCaseId}</span>
                    <span className="test-case-title">{tc.title}</span>
                  </label>
                </div>
              ))}
              {folder.children.length > 0 && (
                <div className="subfolder-container">
                  {renderFolderTree(folder.children)}
                </div>
              )}
            </div>
          )}
        </div>
      );
    });
  };

  const filteredAvailableTestCases = searchTerm
    ? availableTestCases.filter(tc =>
        tc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tc.testCaseId.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : availableTestCases;

  const folderTree = buildFolderTree(folders);
  const testCasesWithoutFolder = getTestCasesWithoutFolder().filter(tc =>
    !searchTerm ||
    tc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tc.testCaseId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Debug logging
  console.log('Folders:', folders);
  console.log('Folder tree:', folderTree);
  console.log('Available test cases:', availableTestCases);
  console.log('Test cases without folder:', testCasesWithoutFolder);

  if (loading) {
    return <div className="loading">Loading suite details...</div>;
  }

  if (error) {
    return (
      <div className="test-suite-detail">
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb mb-0" style={{ background: 'transparent', padding: 0 }}>
            <li className="breadcrumb-item">
              <Link to="/projects">Projects</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={`/projects/${projectKey}`}>{projectKey}</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={`/projects/${projectKey}/test-suites`}>Test Suites</Link>
            </li>
            <li className="breadcrumb-item active">Error</li>
          </ol>
        </nav>
        <div className="error-container">
          <p className="error">{error}</p>
        </div>
      </div>
    );
  }

  if (!suite) {
    return <div className="error">Suite not found</div>;
  }

  return (
    <div className="test-suite-detail">
      <div className="page-header">
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb mb-0" style={{ background: 'transparent', padding: 0 }}>
            <li className="breadcrumb-item">
              <Link to="/projects">Projects</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={`/projects/${projectKey}`}>{projectKey}</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={`/projects/${projectKey}/test-suites`}>Test Suites</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">{suite.name}</li>
          </ol>
        </nav>
        <h1>Test Suite: {suite.name}</h1>
        {suite.description && <p className="suite-description">{suite.description}</p>}
      </div>

      <div className="suite-stats">
        <div className="stat-card">
          <div className="stat-value">{linkedTestCases.length}</div>
          <div className="stat-label">Linked Test Cases</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{availableTestCases.length}</div>
          <div className="stat-label">Available to Link</div>
        </div>
      </div>

      <div className="suite-actions">
        {/* Moved Link button into the Linked Test Cases header for horizontal alignment */}
      </div>

      <div className="linked-test-cases">
        <div className="linked-test-cases-header">
          <h2>Linked Test Cases</h2>
          <div className="linked-actions">
            <button
              className="btn btn-danger"
              onClick={handleUnlinkAll}
              disabled={isUnlinking || linkedTestCases.length === 0}
            >
              {isUnlinking ? (
                <><span className="spinner"></span> Unlinking...</>
              ) : (
                'Unlink All'
              )}
            </button>
            <button className="btn btn-primary" onClick={() => setShowLinkModal(true)}>
              + Link Test Cases
            </button>
          </div>
        </div>
        {linkedTestCases.length === 0 ? (
          <div className="empty-state">
            <p>No test cases linked to this suite yet.</p>
            <button className="btn btn-primary" onClick={() => setShowLinkModal(true)}>
              Link Test Cases
            </button>
          </div>
        ) : (
          <table className="test-cases-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Title</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {linkedTestCases.map(tc => (
                <tr key={tc._id || tc.id}>
                  <td>{tc.testCaseId}</td>
                  <td>
                    <a href={`/projects/${projectKey}/test-cases/${tc._id || tc.id}`}>
                      {tc.title}
                    </a>
                  </td>
                  <td><span className={`priority-badge priority-${tc.priority?.toLowerCase()}`}>{tc.priority}</span></td>
                  <td><span className={`status-badge status-${tc.status?.toLowerCase()}`}>{tc.status}</span></td>
                  <td>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleUnlinkTestCase(tc._id || tc.id)}
                    >
                      Unlink
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Link Test Cases Modal */}
      {showLinkModal && (
        <div className="modal-overlay" onClick={() => setShowLinkModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Link Test Cases to {suite.name}</h2>
              <button className="modal-close" onClick={() => setShowLinkModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search test cases..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="test-cases-tree">
                {folderTree.length === 0 && testCasesWithoutFolder.length === 0 ? (
                  <p className="empty-message">No available test cases to link</p>
                ) : (
                  <>
                    {folderTree.length > 0 && renderFolderTree(folderTree)}
                    
                    {testCasesWithoutFolder.length > 0 && (
                      <div className="folder-item">
                        <div className="folder-header" onClick={() => toggleFolder('no-folder')}>
                          <span className="folder-icon">{expandedFolders.has('no-folder') ? '📂' : '📁'}</span>
                          <span className="folder-name">Uncategorized</span>
                          <span className="folder-count">({testCasesWithoutFolder.length})</span>
                        </div>
                        {expandedFolders.has('no-folder') && (
                          <div className="folder-content">
                            {testCasesWithoutFolder.map(tc => (
                              <div key={tc._id || tc.id} className="test-case-item">
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={selectedTestCases.includes(tc._id || tc.id)}
                                    onChange={() => toggleTestCaseSelection(tc._id || tc.id)}
                                  />
                                  <span className="test-case-id">{tc.testCaseId}</span>
                                  <span className="test-case-title">{tc.title}</span>
                                </label>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowLinkModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleLinkTestCases}
                disabled={selectedTestCases.length === 0 || isLinking}
              >
                {isLinking ? (
                  <>
                    <span className="spinner"></span>
                    Linking...
                  </>
                ) : (
                  `Link ${selectedTestCases.length} Test Case${selectedTestCases.length !== 1 ? 's' : ''}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          <span>{toast.message}</span>
          <button className="toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}
    </div>
  );
}

export default TestSuiteDetail;
